import React, { useState, useRef } from "react";
import { ChevronRight, Building2, Share } from "lucide-react";
import {
  SidebarSimpleIcon,
  CaretUpIcon,
  CaretDownIcon,
  WarningIcon,
  CaretRightIcon,
  HeartStraightIcon,
} from "@phosphor-icons/react";
import { Tooltip } from "react-tooltip";
import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import { useLoadSpaceAccessibilityTagsPublic } from "common/hooks/tagging/useLoadSpaceAccessibilityTagsPublic";
import { bucketUrlPrefix } from "common/config";
import Loader from "@/components/common/Loader";
import SpaceAlertsBanner from "@/components/common/SpaceAlertsBanner";
import { Swiper, SwiperSlide } from "swiper/react";
import FullImageViewer from "@/components/FullImageViewer";
import FlagVenuePhoto from "@/features/flag-report/FlagVenuePhoto";
import FlagSpaceAccessibility from "@/features/flag-report/FlagSpaceAccessibility";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { RWebShare } from "react-web-share";
import AnalyticsService from "@/services/analytics";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { PiFlagPennant } from "react-icons/pi";

interface SpaceAccessibilityDetailsProps {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  space: any;
  categoryName: string;
  onBack: () => void;
  onNavigateToSpace?: (spaceId: number) => void;
  onPreviousSpace?: () => void;
  onNextSpace?: () => void;
  hasPreviousSpace?: boolean;
  hasNextSpace?: boolean;
  showSidebar?: boolean;
  setShowSidebar?: (show: boolean) => void;
  pageName?: string; // For analytics tracking
}

export default function SpaceAccessibilityDetails({
  venueDetailsResponse: data,
  space,
  categoryName,
  onBack,
  onNavigateToSpace,
  onPreviousSpace,
  onNextSpace,
  hasPreviousSpace = false,
  hasNextSpace = false,
  showSidebar,
  setShowSidebar,
  pageName = "Space Accessibility Details",
}: SpaceAccessibilityDetailsProps) {
  const i18n = useI18N();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullImageView, setFullImageView] = useState(false);

  // Determine event names based on pageName
  const getEventName = (baseName: string) => {
    if (pageName === "Accessibility Space Guide") {
      const eventMap: Record<string, string> = {
        spaceAccessibilityPreviousClicked:
          "accessibilitySpaceGuideSpacePreviousClicked",
        spaceAccessibilityNextClicked:
          "accessibilitySpaceGuideSpaceNextClicked",
        spaceAccessibilityConnectedSpaceClicked:
          "accessibilitySpaceGuideConnectedSpaceClicked",
        spaceAccessibilityFlagClicked:
          "accessibilitySpaceGuideSpaceFlagClicked",
        spaceAccessibilityShareClicked:
          "accessibilitySpaceGuideSpaceShareClicked",
        spaceAccessibilityShare: "accessibilitySpaceGuideSpaceShareClicked",
        flagPhotoReportClose: "accessibilitySpaceGuideFlagClosed",
        flagPhotoReportOpen: "accessibilitySpaceGuideFlagOpened",
        flagSpaceAccessibilityOpen: "accessibilitySpaceGuideFlagOpened",
        flagSpaceAccessibilityClose: "accessibilitySpaceGuideFlagClosed",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };
  const swiperRef = useRef<any>(null);

  // Flag photo state
  const [isFlagVenuePhotoOpen, setIsFlagVenuePhotoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);

  // Flag space accessibility state
  const [isFlagSpaceAccessibilityOpen, setIsFlagSpaceAccessibilityOpen] =
    useState(false);

  // Connected spaces dropdown state
  const [showConnectedSpaces, setShowConnectedSpaces] = useState(false);

  // Alerts expanded state
  const [isAlertsExpanded, setIsAlertsExpanded] = useState(false);

  // Auth hooks
  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const isMapper = !!(
    user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam
  );

  // Prepare space data for API call
  const spaceData = React.useMemo(() => {
    if (!space || !data) return null;

    try {
      return {
        space,
        categoryName,
        venueId: data.details.business.id.toString(),
        googleBusinessId: data.details.business.googleBusinessId || "",
        reviewId: space.level_review_id?.toString() || "",
        reviewLevelId: space.id?.toString() || "",
      };
    } catch (error) {
      return null;
    }
  }, [space, data, categoryName]);

  // Load space accessibility details using the public /review/display-space-tags API (no authentication required)
  const {
    data: accessibilityData,
    isLoading,
    error,
  } = useLoadSpaceAccessibilityTagsPublic({
    businessId: spaceData?.venueId || "",
    googleBusinessId: spaceData?.googleBusinessId || "",
    reviewId: spaceData?.reviewId || "",
    reviewLevelId: spaceData?.reviewLevelId || "",
    enabled: !!spaceData && !!spaceData.reviewId && !!spaceData.reviewLevelId,
  });

  // Extract level data from API response
  const levelData = React.useMemo(() => {
    if (!accessibilityData?.data?.filterOptions) return null;

    // Find the level data that matches our space
    for (const category of accessibilityData.data.filterOptions) {
      if (category.levelOptions) {
        const matchingLevel = category.levelOptions.find(
          (level: any) =>
            level.id === spaceData?.space?.id ||
            level.level_review_id === spaceData?.space?.level_review_id,
        );
        if (matchingLevel) {
          return matchingLevel;
        }
      }
    }
    return null;
  }, [accessibilityData, spaceData]);

  // Group accessibility tags by display section
  const groupedTags = React.useMemo(() => {
    if (!levelData?.questionOptions) return {};

    const groups: Record<string, any[]> = {};
    levelData.questionOptions.forEach((question: any) => {
      const sectionName = question.category_display_section_name || "Other";
      if (!groups[sectionName]) {
        groups[sectionName] = [];
      }
      if (question.tagOptions && question.tagOptions.length > 0) {
        groups[sectionName].push(question);
      }
    });
    return groups;
  }, [levelData]);

  // Get photos from level data
  const photos = levelData?.levelPhotos || [];

  // Get connected spaces data
  const connectedSpaces = React.useMemo(() => {
    if (!levelData?.connectedLevelData) return [];
    return levelData.connectedLevelData;
  }, [levelData]);

  // Get alerts linked to this specific space
  const spaceAlerts = React.useMemo(() => {
    if (!data?.details?.businessNotesAll || !space?.id) return [];
    return data.details.businessNotesAll.filter(
      (note) => note.status === "Open" && note.location_id === space.id,
    );
  }, [data?.details?.businessNotesAll, space?.id]);

  // Close connected spaces dropdown when clicking outside
  React.useEffect(() => {
    if (!showConnectedSpaces) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest("[data-connected-spaces-dropdown]")) {
        setShowConnectedSpaces(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showConnectedSpaces]);

  // Navigation functions for custom buttons
  const handlePrevImage = () => {
    if (swiperRef.current && photos.length > 1) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNextImage = () => {
    if (swiperRef.current && photos.length > 1) {
      swiperRef.current.slideNext();
    }
  };

  const handleOpenFullImage = () => {
    setFullImageView(true);
    // Remove focus from the button to prevent blue border after closing modal
    (document.activeElement as HTMLElement)?.blur();
  };

  const handleFlagVenuePhotoClose = () => {
    const eventName = getEventName("flagPhotoReportClose");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
    setIsFlagVenuePhotoOpen(false);

    // Ensure body scroll is restored
    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const handleOpenFlagTracking = () => {
    const eventName = getEventName("flagPhotoReportOpen");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
  };

  const handleFlagPhoto = (image: any) => {
    handleOpenFlagTracking();

    if (!user || !isLoggedIn) {
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setSelectedImage(image);
    setIsFlagVenuePhotoOpen(true);
  };

  const handleFlagSpaceAccessibility = () => {
    const eventName = getEventName("flagSpaceAccessibilityOpen");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {
      VenueId: data?.details?.id,
      VenueName: data?.details?.name,
      SpaceName: space?.level,
    });

    if (!user || !isLoggedIn) {
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setIsFlagSpaceAccessibilityOpen(true);
  };

  const handleFlagSpaceAccessibilityClose = () => {
    const eventName = getEventName("flagSpaceAccessibilityClose");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
    setIsFlagSpaceAccessibilityOpen(false);

    // Ensure body scroll is restored
    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const handleCloseFullImageViewer = () => {
    setFullImageView(false);

    // Ensure body scroll is restored
    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const handleShare = () => {
    const eventName = getEventName("spaceAccessibilityShare");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {
      VenueId: data?.details?.id,
      VenueName: data?.details?.name,
      SpaceName: space?.level,
      SpaceId: space?.id,
    });
  };

  if (!spaceData) {
    return (
      <div className="text-center py-8">
        <p className="text-fg-neutral-subtle">Space data not available</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border-token-brand-minimal px-4 md:pl-5 flex-shrink-0 bg-bg-brand-secondary-default">
        {/* Desktop layout (lg+) — single-row header (existing) */}
        <div className="hidden lg:flex items-center justify-between gap-2 md:gap-3">
          {/* Sidebar Toggle Button */}
          {setShowSidebar !== undefined && showSidebar !== undefined && (
            <button
              id="category-sidebar-toggle-space-detail-btn"
              type="button"
              onClick={() => setShowSidebar(!showSidebar)}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
              aria-label="Toggle categories sidebar"
              aria-expanded={showSidebar}
            >
              <SidebarSimpleIcon size={20} className="text-fg-neutral-strong" />
            </button>
          )}
          {setShowSidebar !== undefined && showSidebar !== undefined && (
            <Tooltip
              anchorSelect="#category-sidebar-toggle-space-detail-btn"
              content={
                showSidebar
                  ? i18n.get("hideCategories")
                  : i18n.get("showCategories")
              }
              place="bottom"
              style={{ zIndex: 9999 }}
            />
          )}

          {/* Vertical Divider */}
          {setShowSidebar !== undefined && showSidebar !== undefined && (
            <div
              className="hidden lg:block w-px h-6 border border-border-token-neutral-minimal"
              aria-hidden="true"
            />
          )}

          {/* Space Navigation Arrows - Left of space name */}
          <div className="flex items-center gap-2">
            <button
              id="space-accessibility-prev-space-btn"
              type="button"
              onClick={
                hasPreviousSpace
                  ? () => {
                      const eventName = getEventName(
                        "spaceAccessibilityPreviousClicked",
                      );
                      AnalyticsService.trackGAEventHandler(eventName);
                      AnalyticsService.trackMx(eventName, {});
                      onPreviousSpace?.();
                    }
                  : undefined
              }
              disabled={!hasPreviousSpace}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active focus:bg-bg-brand-ghost-focus disabled:bg-bg-brand-ghost-disabled"
              aria-label="Previous space"
            >
              <CaretUpIcon size={20} className="text-fg-neutral-strong" />
            </button>
            <Tooltip
              anchorSelect="#space-accessibility-prev-space-btn"
              content={i18n.get("previousSpace")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
            <button
              id="space-accessibility-next-space-btn"
              type="button"
              onClick={
                hasNextSpace
                  ? () => {
                      const eventName = getEventName(
                        "spaceAccessibilityNextClicked",
                      );
                      AnalyticsService.trackGAEventHandler(eventName);
                      AnalyticsService.trackMx(eventName, {});
                      onNextSpace?.();
                    }
                  : undefined
              }
              disabled={!hasNextSpace}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active focus:bg-bg-brand-ghost-focus disabled:bg-bg-brand-ghost-disabled"
              aria-label="Next space"
            >
              <CaretDownIcon size={20} className="text-fg-neutral-strong" />
            </button>
            <Tooltip
              anchorSelect="#space-accessibility-next-space-btn"
              content={i18n.get("nextSpace")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
          </div>

          <div className="flex-1 min-w-0 flex items-center justify-between">
            <h2 className="flex flex-1 title-m-bold text-fg-neutral-strong truncate">
              {levelData?.level || spaceData.space.level}
            </h2>
            {/* Connected Spaces */}
            {connectedSpaces.length > 0 && (
              <div className="relative" data-connected-spaces-dropdown>
                <button
                  type="button"
                  onClick={() => setShowConnectedSpaces(!showConnectedSpaces)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowConnectedSpaces(false);
                    }
                  }}
                  aria-label={`${connectedSpaces.length} connected ${connectedSpaces.length > 1 ? "spaces" : "space"}`}
                  aria-expanded={showConnectedSpaces}
                  aria-haspopup="true"
                  className="flex items-center gap-2 text-fg-neutral-strong hover:text-fg-brand-strong transition-colors focus-blue"
                >
                  {/* Building Icon */}
                  <Building2 size={24} aria-hidden="true" />
                  <span className="label-m-medium">
                    {connectedSpaces.length}{" "}
                    {connectedSpaces.length > 1
                      ? i18n.get("connectedSpaces")
                      : i18n.get("connectedSpace")}
                  </span>
                  {/* Down Arrow */}
                  <ChevronRight
                    size={18}
                    className={`transform transition-transform ${showConnectedSpaces ? "-rotate-90" : "rotate-90"}`}
                    aria-hidden="true"
                  />
                </button>

                {/* Dropdown */}
                <div
                  className={`absolute right-0 top-full mt-1 w-64 md:w-80 bg-bg-brand-secondary-default border border-neutral-100 rounded-xl shadow-lg z-50 transition-all duration-200 ${showConnectedSpaces ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
                >
                  <div className="p-3">
                    <div className="space-y-1">
                      {connectedSpaces.map((space, index) => (
                        <button
                          key={space.connected_level_id || index}
                          type="button"
                          onClick={() => {
                            // Analytics tracking
                            const eventName = getEventName(
                              "spaceAccessibilityConnectedSpaceClicked",
                            );
                            AnalyticsService.trackGAEventHandler(eventName);
                            AnalyticsService.trackMx(eventName, {
                              ConnectedSpaceName: space.connected_level_name,
                              ConnectedSpaceId: space.connected_level_id,
                            });
                            if (onNavigateToSpace && space.connected_level_id) {
                              onNavigateToSpace(space.connected_level_id);
                            }
                            setShowConnectedSpaces(false);
                          }}
                          aria-label={`Navigate to ${space.connected_level_name}`}
                          className="w-full flex items-center justify-between p-2 rounded-md hover:bg-bg-neutral-secondary-hover transition-colors text-left focus-blue"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="label-m-medium text-fg-neutral-strong leading-relaxed">
                              {space.connected_level_name}
                            </span>
                          </div>
                          <ChevronRight
                            size={16}
                            className="text-fg-neutral-strong flex-shrink-0 ml-2"
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile/tablet layout (< lg) — title, actions, alerts on their own rows */}
        <div className="lg:hidden flex flex-col gap-2 py-2">
          {/* Row A — navigation: sidebar toggle + prev/next arrows */}
          <div className="flex items-center gap-2 border-b border-border-token-brand-minimal">
            {setShowSidebar !== undefined && showSidebar !== undefined && (
              <>
                <button
                  id="category-sidebar-toggle-space-detail-btn-mobile"
                  type="button"
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
                  aria-label="Toggle categories sidebar"
                  aria-expanded={showSidebar}
                >
                  <SidebarSimpleIcon
                    size={20}
                    className="text-fg-neutral-strong"
                  />
                </button>
                <Tooltip
                  anchorSelect="#category-sidebar-toggle-space-detail-btn-mobile"
                  content={
                    showSidebar
                      ? i18n.get("hideCategories")
                      : i18n.get("showCategories")
                  }
                  place="bottom"
                  style={{ zIndex: 9999 }}
                />
              </>
            )}
            <button
              id="space-accessibility-prev-space-btn-mobile"
              type="button"
              onClick={
                hasPreviousSpace
                  ? () => {
                      const eventName = getEventName(
                        "spaceAccessibilityPreviousClicked",
                      );
                      AnalyticsService.trackGAEventHandler(eventName);
                      AnalyticsService.trackMx(eventName, {});
                      onPreviousSpace?.();
                    }
                  : undefined
              }
              disabled={!hasPreviousSpace}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active focus:bg-bg-brand-ghost-focus disabled:bg-bg-brand-ghost-disabled"
              aria-label="Previous space"
            >
              <CaretUpIcon size={20} className="text-fg-neutral-strong" />
            </button>
            <Tooltip
              anchorSelect="#space-accessibility-prev-space-btn-mobile"
              content={i18n.get("previousSpace")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
            <button
              id="space-accessibility-next-space-btn-mobile"
              type="button"
              onClick={
                hasNextSpace
                  ? () => {
                      const eventName = getEventName(
                        "spaceAccessibilityNextClicked",
                      );
                      AnalyticsService.trackGAEventHandler(eventName);
                      AnalyticsService.trackMx(eventName, {});
                      onNextSpace?.();
                    }
                  : undefined
              }
              disabled={!hasNextSpace}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active focus:bg-bg-brand-ghost-focus disabled:bg-bg-brand-ghost-disabled"
              aria-label="Next space"
            >
              <CaretDownIcon size={20} className="text-fg-neutral-strong" />
            </button>
            <Tooltip
              anchorSelect="#space-accessibility-next-space-btn-mobile"
              content={i18n.get("nextSpace")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
          </div>

          {/* Row B — space title */}
          <h2 className="title-m-bold text-fg-neutral-strong">
            {levelData?.level || spaceData.space.level}
          </h2>

          {/* Row C — actions: flag, share, decorative heart + connected-space dropdown */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                id="space-accessibility-flag-btn-mobile"
                onClick={handleFlagSpaceAccessibility}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover transition-colors"
                aria-label="Report accessibility issue"
              >
                <PiFlagPennant
                  size={20}
                  className="text-fg-neutral-strong"
                  aria-hidden="true"
                />
              </button>
              <Tooltip
                anchorSelect="#space-accessibility-flag-btn-mobile"
                content={i18n.get("reportAnIssue")}
                place="bottom"
              />

              <RWebShare
                data={{
                  text: `Check out the accessibility details for ${space?.level || "this space"}`,
                  url: window.location.href,
                  title: `${space?.level || "Space"} - ${data?.details?.name || "Venue"}`,
                }}
                onClick={handleShare}
              >
                <button
                  id="space-accessibility-share-btn-mobile"
                  type="button"
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover transition-colors"
                  aria-label="Share space accessibility"
                >
                  <Share size={16} className="text-fg-neutral-strong" />
                </button>
              </RWebShare>
              <Tooltip
                anchorSelect="#space-accessibility-share-btn-mobile"
                content="Share"
                place="bottom"
              />

              {/* Decorative heart placeholder — wiring deferred to a follow-up */}
              {/* <div
                className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal"
                aria-hidden="true"
              >
                <HeartStraightIcon size={20} className="text-fg-neutral-strong" />
              </div> */}
            </div>

            {connectedSpaces.length > 0 && (
              <div className="relative" data-connected-spaces-dropdown>
                <button
                  type="button"
                  onClick={() => setShowConnectedSpaces(!showConnectedSpaces)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowConnectedSpaces(false);
                    }
                  }}
                  aria-label={`${connectedSpaces.length} connected ${connectedSpaces.length > 1 ? "spaces" : "space"}`}
                  aria-expanded={showConnectedSpaces}
                  aria-haspopup="true"
                  className="flex items-center gap-2 px-3 py-2 rounded-[8px] border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover label-m-medium text-fg-neutral-strong focus-blue"
                >
                  <span>{i18n.get("connectedSpace")}</span>
                  <CaretDownIcon
                    size={16}
                    aria-hidden="true"
                    className={`transition-transform ${showConnectedSpaces ? "rotate-180" : ""}`}
                  />
                </button>

                <div
                  className={`absolute right-0 top-full mt-1 w-64 md:w-80 bg-bg-brand-secondary-default border border-neutral-100 rounded-xl shadow-lg z-50 transition-all duration-200 ${showConnectedSpaces ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
                >
                  <div className="p-3">
                    <div className="space-y-1">
                      {connectedSpaces.map((connSpace, index) => (
                        <button
                          key={connSpace.connected_level_id || index}
                          type="button"
                          onClick={() => {
                            const eventName = getEventName(
                              "spaceAccessibilityConnectedSpaceClicked",
                            );
                            AnalyticsService.trackGAEventHandler(eventName);
                            AnalyticsService.trackMx(eventName, {
                              ConnectedSpaceName:
                                connSpace.connected_level_name,
                              ConnectedSpaceId: connSpace.connected_level_id,
                            });
                            if (
                              onNavigateToSpace &&
                              connSpace.connected_level_id
                            ) {
                              onNavigateToSpace(connSpace.connected_level_id);
                            }
                            setShowConnectedSpaces(false);
                          }}
                          aria-label={`Navigate to ${connSpace.connected_level_name}`}
                          className="w-full flex items-center justify-between p-2 rounded-md hover:bg-bg-neutral-secondary-hover transition-colors text-left focus-blue"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="label-m-medium text-fg-neutral-strong leading-relaxed">
                              {connSpace.connected_level_name}
                            </span>
                          </div>
                          <ChevronRight
                            size={16}
                            className="text-fg-neutral-strong flex-shrink-0 ml-2"
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Row D — alerts banner */}
          <SpaceAlertsBanner alerts={spaceAlerts} isMapper={isMapper} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col lg:flex-row lg:items-stretch overflow-y-auto">
        {/* Image Section */}
        <div className="relative bg-container-brand-minimal overflow-hidden flex-none h-[300px] md:h-[400px] lg:h-auto lg:flex-1 md:min-h-[400px]">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-fg-neutral-subtle">Failed to load images</p>
            </div>
          ) : photos.length > 0 ? (
            <div className="relative w-full flex flex-col overflow-hidden lg:h-full">
              {/* Swiper Image Container - Fixed height on mobile */}
              <div className="relative overflow-hidden h-[300px] md:h-[400px] lg:h-auto lg:flex-1 rounded-lg lg:rounded-none">
                <style>{`
 .space-image-swiper {
 width: 100% !important;
 height: 100% !important;
 overflow: hidden !important;
 }
 .space-image-swiper .swiper-wrapper {
 width: 100% !important;
 height: 100% !important;
 }
 .space-image-swiper .swiper-slide {
 width: 100% !important;
 height: 100% !important;
 overflow: hidden !important;
 }`}</style>

                <Swiper
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  spaceBetween={0}
                  slidesPerView={1}
                  loop={true}
                  initialSlide={currentImageIndex}
                  onSlideChange={(swiper) =>
                    setCurrentImageIndex(swiper.realIndex)
                  }
                  className="space-image-swiper"
                  style={{
                    width: "100%",
                    height: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  {photos.map((photo, index) => (
                    <SwiperSlide key={index}>
                      <div className="w-full h-full flex items-center justify-center">
                        <button
                          onClick={handleOpenFullImage}
                          className="lg:min-h-[60vh] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg cursor-pointer group relative h-full w-full flex items-center justify-center"
                          aria-label="Open image in full screen"
                        >
                          <img
                            alt={
                              photo.alt_text ||
                              levelData?.level ||
                              i18n.get("spaceImage")
                            }
                            className="object-contain rounded-lg w-auto h-auto max-w-full max-h-full lg:max-h-[60vh]"
                            src={bucketUrlPrefix + photo.path}
                          />
                          {/* Hover gradient overlay */}
                          <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none " />
                        </button>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Carousel Navigation - Left/Right Overlay Buttons */}
                {photos.length > 1 && (
                  <>
                    {/* Previous Button */}
                    <button
                      type="button"
                      onClick={handlePrevImage}
                      className="flex absolute left-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-surface-secondary border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                      aria-label="Previous photo"
                    >
                      <ChevronRight
                        size={18}
                        className="rotate-180 text-fg-neutral-strong"
                      />
                    </button>

                    {/* Next Button */}
                    <button
                      type="button"
                      onClick={handleNextImage}
                      className="flex absolute right-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-surface-secondary border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                      aria-label="Next photo"
                    >
                      <ChevronRight
                        size={18}
                        className="text-fg-neutral-strong"
                      />
                    </button>
                  </>
                )}

                {/* Image Counter - Over the image at bottom */}
                <div className="absolute bottom-3 md:bottom-[30px] left-1/2 -translate-x-1/2 z-10 bg-black/70 text-white label-m-medium px-3 py-1 rounded-full font-medium">
                  {currentImageIndex + 1}/{photos.length}
                </div>
              </div>

              {/* Mobile Alt Text - Below Image Container (outside fixed height) */}
              {photos[currentImageIndex]?.alt_text && (
                <div className="lg:hidden px-4 py-2 bg-bg-brand-secondary-default border-t border-border-token-neutral-minimal min-h-[50px]">
                  <p className="label-s-medium text-fg-neutral-strong text-center leading-relaxed">
                    {photos[currentImageIndex].alt_text}
                  </p>
                </div>
              )}

              {/* Fixed Control Panel - Desktop only, showing alt text */}
              <div className="hidden lg:flex flex-shrink-0 bg-bg-brand-secondary-default backdrop-blur-sm border-t border-gray-200 shadow-lg flex-col">
                {/* Alt Text Section - Fixed height to prevent layout shift */}
                <div className="px-4 py-2 h-20 flex items-center justify-center">
                  {photos[currentImageIndex]?.alt_text && (
                    <p className="label-s-medium text-fg-neutral-strong text-center leading-relaxed line-clamp-3 overflow-y-auto">
                      {photos[currentImageIndex].alt_text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-fg-neutral-subtle">
                {i18n.get("noImagesAvailable")}
              </p>
            </div>
          )}
        </div>

        {/* Accessibility Details */}
        <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-border-token-brand-minimal bg-bg-brand-secondary-default lg:h-full flex flex-col">
          <div className="flex flex-col lg:h-full">
            <div className="flex-shrink-0 flex items-center justify-between px-3 md:px-4 pt-3 md:pt-4 pb-3 border-b border-border-token-brand-minimal">
              <h3 className="label-l-bold text-fg-neutral-strong">
                {i18n.get("accessibility")}
              </h3>
              {/* Aside flag/share cluster — desktop only; mobile/tablet uses Row C in the page header */}
              <div className="hidden lg:flex items-center gap-2">
                {/* Flag Button */}
                <button
                  id="space-accessibility-flag-btn"
                  onClick={handleFlagSpaceAccessibility}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover transition-colors"
                  aria-label="Report accessibility issue"
                >
                  <PiFlagPennant
                    size={20}
                    className="text-fg-neutral-strong"
                    aria-hidden="true"
                  />
                </button>
                <Tooltip
                  anchorSelect="#space-accessibility-flag-btn"
                  content={i18n.get("reportAnIssue")}
                  place="bottom"
                />

                {/* Share Button */}
                <RWebShare
                  data={{
                    text: `Check out the accessibility details for ${space?.level || "this space"}`,
                    url: window.location.href,
                    title: `${space?.level || "Space"} - ${data?.details?.name || "Venue"}`,
                  }}
                  onClick={handleShare}
                >
                  <button
                    id="space-accessibility-share-btn"
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover transition-colors"
                    aria-label="Share space accessibility"
                  >
                    <Share size={16} className="text-fg-neutral-strong" />
                  </button>
                </RWebShare>
                <Tooltip
                  anchorSelect="#space-accessibility-share-btn"
                  content="Share"
                  place="bottom"
                />
              </div>
            </div>

            <div className="lg:flex-1 lg:overflow-y-auto p-4">
              {/* Accessibility Alerts for this space — desktop only; mobile/tablet uses Row D (SpaceAlertsBanner) in the page header */}
              {spaceAlerts.length > 0 && (
                <section
                  aria-labelledby="space-alerts-heading"
                  className="hidden lg:block mb-3"
                >
                  <div className="bg-bg-error-tertiary-default rounded-lg border border-border-token-error-minimal hover:bg-bg-error-tertiary-hover">
                    <button
                      type="button"
                      onClick={() => setIsAlertsExpanded(!isAlertsExpanded)}
                      className="w-full flex items-center justify-between p-3 hover:bg-error-100 transition-colors rounded-lg focus-blue"
                      aria-expanded={isAlertsExpanded}
                      aria-controls="space-alerts-list"
                    >
                      <div className="flex items-center gap-3">
                        <WarningIcon
                          size={24}
                          weight="fill"
                          aria-hidden="true"
                          className="text-fg-error-subtle"
                        />
                        <h3
                          id="space-alerts-heading"
                          className="label-m-semibold text-fg-neutral-strong"
                        >
                          {i18n.get("accessibilityAlerts")}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="label-s-medium text-fg-neutral-strong">
                          {spaceAlerts.length}
                        </span>
                        <CaretRightIcon
                          size={16}
                          aria-hidden="true"
                          weight="bold"
                          className={`text-fg-neutral-strong transition-transform ${isAlertsExpanded ? "rotate-90" : ""}`}
                        />
                      </div>
                    </button>

                    {isAlertsExpanded && (
                      <ul
                        id="space-alerts-list"
                        className="px-3 pb-3 space-y-3"
                      >
                        {spaceAlerts.map((alert) => (
                          <li key={alert.business_note_text_id}>
                            <article className="bg-bg-brand-secondary-default rounded-lg p-4">
                              <div className="mb-2">
                                <p className="label-s-medium">
                                  {alert.formatedScheduleDateFrom &&
                                    alert.formatedScheduleDateTo && (
                                      <>
                                        <span className="text-blue-600">
                                          {alert.formatedScheduleDateFrom} to{" "}
                                          {alert.formatedScheduleDateTo}
                                        </span>
                                        <span> • </span>
                                      </>
                                    )}
                                  <span className="text-fg-neutral-strong">
                                    {(() => {
                                      const roles =
                                        alert.business_note_by_user_roles;
                                      const authorName = isMapper
                                        ? alert.business_note_by_user
                                        : roles?.isSociabilityMapper ||
                                            roles?.isSociabilityTeam
                                          ? "Sociability team"
                                          : roles
                                            ? alert.business_note_by_user
                                            : "Sociability team";
                                      return `Added on ${alert.formatedCreatedAt} by ${authorName}`;
                                    })()}
                                  </span>
                                </p>
                              </div>
                              <h4 className="label-m-semibold text-fg-neutral-strong mb-2">
                                {alert.business_note_title}
                              </h4>
                              {alert.business_note_text && (
                                <p className="label-s-regular text-fg-neutral-strong">
                                  {alert.business_note_text}
                                </p>
                              )}
                            </article>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              )}

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-fg-neutral-subtle">
                    Failed to load accessibility details
                  </p>
                </div>
              ) : Object.keys(groupedTags).length > 0 ? (
                <div className="flex flex-col gap-3">
                  {Object.entries(groupedTags).map(
                    ([sectionName, questions], sectionIndex, entries) => (
                      <React.Fragment key={sectionName}>
                        <div className="flex flex-col gap-1">
                          <h4 className="body-m-medium text-fg-brand-subtle">
                            {sectionName}
                          </h4>

                          <ul className="flex flex-col gap-1 list-disc list-inside pl-2">
                            {(questions as any[]).flatMap(
                              (question: any, qIdx: number) =>
                                question.tagOptions.map(
                                  (tag: any, tIdx: number) => (
                                    <li
                                      key={`${qIdx}-${tIdx}`}
                                      className="body-m-medium text-fg-neutral-strong"
                                    >
                                      {tag.optionTitle}
                                    </li>
                                  ),
                                ),
                            )}
                          </ul>
                        </div>

                        {sectionIndex < entries.length - 1 && (
                          <hr
                            className="border-border-token-brand-minimal"
                            aria-hidden="true"
                          />
                        )}
                      </React.Fragment>
                    ),
                  )}

                  {/* Notes Section */}
                  {(levelData as any)?.notes && (
                    <div>
                      <hr
                        className="border-border-token-brand-minimal py-1"
                        aria-hidden="true"
                      />
                      <h4 className="title-s-semibold text-fg-neutral-strong mb-1 md:mb-2">
                        {i18n.get("notes")}
                      </h4>
                      <p className="label-m-medium text-fg-neutral-strong leading-relaxed">
                        {(levelData as any).notes}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-fg-neutral-subtle">
                    {i18n.get("noAccessibilityInformationAvailable")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full image viewer */}
      {fullImageView && photos.length > 0 && (
        <FullImageViewer
          images={photos.map((photo) => ({
            src: bucketUrlPrefix + photo.path,
            alt: photo.alt_text || levelData?.level || i18n.get("spaceImage"),
            tag_name: levelData?.level || i18n.get("unknownSpace"),
            category_name: categoryName,
            created_at: photo.created_at,
            photo_type: photo.photo_type,
            review_photos_id: photo.review_photos_id?.toString(),
            width: photo.width,
            height: photo.height,
            sort_order: photo.sort_order,
            qtags:
              levelData?.questionOptions?.flatMap(
                (question: any) =>
                  question.tagOptions?.map((tag: any) => ({
                    tagId: tag.optionTitleId || 0,
                    tagName: tag.optionTitle || "",
                    categoryDisplaySectionId:
                      question.category_display_section_id?.toString() ||
                      "accessibility-features",
                  })) || [],
              ) || [],
            tags: [
              {
                tagName: levelData?.level || i18n.get("unknownSpace"),
                categoryDisplaySectionId: "space-info",
              },
            ],
            displaySections: (() => {
              const sections: Record<string, string> = {};

              if (levelData?.questionOptions) {
                levelData.questionOptions.forEach((question: any) => {
                  const sectionName =
                    question.category_display_section_name ||
                    i18n.get("accessibilityFeatures");
                  const sectionId =
                    question.category_display_section_id?.toString() ||
                    "accessibility-features";
                  sections[sectionId] = sectionName;
                });
              }

              // Add space info section
              sections["space-info"] = i18n.get("spaceInformation");

              return sections;
            })(),
            venueId: data?.details?.id,
            venueName: data?.details?.name,
            venueAddress: data?.details?.address,
            user_roles: {
              isGeneralUser: false,
            },
          }))}
          currentIndex={currentImageIndex}
          onClose={handleCloseFullImageViewer}
          onFlag={handleFlagPhoto}
          showFlag={true}
          showRightTab={true}
        />
      )}

      {/* Flag Photo Modal */}
      <FlagVenuePhoto
        photoReviewId={selectedImage?.review_photos_id}
        venueName={data?.details?.name}
        isOpen={isFlagVenuePhotoOpen}
        onClose={() => handleFlagVenuePhotoClose()}
        venueDetails={{
          data: {
            id: data?.details?.id,
            brandId: data?.details?.brandId,
            brandName: data?.details?.brandName,
            address: data?.details?.address,
            name: data?.details?.name,
            types: data?.details?.types,
            business_photos: data?.details?.businessPhotos,
          },
        }}
      />

      {/* Flag Space Accessibility Modal */}
      <FlagSpaceAccessibility
        reviewLevelId={spaceData?.reviewLevelId || ""}
        businessId={spaceData?.venueId || ""}
        spaceName={space?.level || "this space"}
        venueName={data?.details?.name || ""}
        isOpen={isFlagSpaceAccessibilityOpen}
        onClose={handleFlagSpaceAccessibilityClose}
        venueDetails={{
          id: data?.details?.id,
          brandId: data?.details?.brandId,
          brandName: data?.details?.brandName,
          address: data?.details?.address,
          name: data?.details?.name,
          types: data?.details?.types,
        }}
      />
    </div>
  );
}
